import {
  Form,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  Divider,
  Switch,
} from "antd";
import { useEffect } from "react";
import { Plus, Trash2, Lock } from "lucide-react";
import { textInputValidator } from "commons/helper";
import { areAllTicketsFree } from "pages/Events/commons/events.helpers";

/**
 * "Charge platform fee to attendees" switch. Shown only to vendors ShoUp has
 * allowed to waive the fee, when the event is waived (a published event, or a
 * draft whose permission couldn't be checked), or when a waived draft was just
 * switched back on because the vendor can no longer waive it (`reset`).
 * When every ticket is free there is no fee to waive: the fee stays on and the
 * switch is locked until a ticket has a price.
 */
function PlatformFeeSwitch({ form, canWaivePlatformFee, locked, reset }) {
  const chargePlatformFee = Form.useWatch("charge_platform_fee", {
    form,
    preserve: true,
  });
  const tickets = Form.useWatch("tickets", { form, preserve: true });
  const free = !locked && areAllTicketsFree(tickets);

  useEffect(() => {
    if (free && chargePlatformFee === false) {
      form.setFieldValue("charge_platform_fee", true);
    }
  }, [free, chargePlatformFee, form]);

  const waived = chargePlatformFee === false;
  if (!canWaivePlatformFee && !waived && !reset) return null;

  // Switching the fee back on is always allowed before publishing; switching
  // it off needs the vendor's permission and at least one paid ticket.
  const disabled = locked || free || (!canWaivePlatformFee && !waived);

  return (
    <div className="rounded-xl border p-4 bg-muted/20 space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold">Platform Fee</p>
          <p className="text-xs text-muted-foreground">
            Charge the platform fee to attendees
          </p>
        </div>
        <Form.Item name="charge_platform_fee" valuePropName="checked" noStyle>
          <Switch
            checkedChildren="Yes"
            unCheckedChildren="No"
            disabled={disabled}
          />
        </Form.Item>
      </div>

      {locked ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Lock className="h-4 w-4" />
          The platform fee setting can&apos;t be changed after the event is
          published.
        </div>
      ) : free ? (
        <div className="text-xs text-muted-foreground">
          Tickets are free, so there&apos;s no platform fee.
        </div>
      ) : waived ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Attendees won&apos;t pay the platform fee on paid tickets for this
          event. It will be deducted from your earnings instead. You can&apos;t
          change this after publishing.
        </div>
      ) : (
        reset && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            Your account can no longer waive the platform fee, so it has been
            switched back on for this event. Attendees will pay it.
          </div>
        )
      )}
    </div>
  );
}

function Step4Tickets({
  form,
  isPublished,
  originalTickets = [],
  canWaivePlatformFee = false,
  platformFeeLocked = false,
  platformFeeReset = false,
}) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Tickets & Contact</h2>

      {isPublished && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          For existing tickets you can update the price and description and
          increase the quantity, but the tier name cannot be changed and the
          tier cannot be removed. You can also add new ticket tiers.
        </div>
      )}

      <div className="rounded-xl border p-4 bg-muted/20 space-y-3">
        <p className="text-sm font-semibold">Event Contact</p>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="contact_email"
              label="Contact Email"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Contact email is required",
                },
                { type: "email", message: "Enter a valid email" },
              ]}
            >
              <Input placeholder="events@example.com" maxLength={50} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="contact_phone"
              label="Contact Phone"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Contact phone is required",
                },
                {
                  pattern: /^[0-9]{10}$/,
                  message: "Enter a valid 10-digit phone number",
                },
              ]}
            >
              <Input placeholder="9876543210" maxLength={10} showCount />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <PlatformFeeSwitch
        form={form}
        canWaivePlatformFee={canWaivePlatformFee}
        locked={platformFeeLocked || isPublished}
        reset={platformFeeReset}
      />

      <Divider plain>
        <span className="text-sm font-semibold">Ticket Tiers</span>
      </Divider>

      <Form.Item
        name="tickets_validator"
        noStyle
        rules={[
          {
            validator() {
              const items = form.getFieldValue("tickets") ?? [];
              return items.length > 0
                ? Promise.resolve()
                : Promise.reject(new Error("Add at least one ticket tier."));
            },
          },
        ]}
      >
        <input type="hidden" />
      </Form.Item>

      <Form.List
        name="tickets"
        initialValue={[
          {
            name: "",
            description: "",
            price: "",
            quantity: "",
            max_concurrent_bookings: 10,
          },
        ]}
      >
        {(fields, { add, remove }) => {
          const revalidate = () =>
            form.validateFields(["tickets_validator"]).catch(() => {});

          return (
            <div className="space-y-4">
              {fields.map(({ key, name }) => {
                const isExisting = name < originalTickets.length;
                const origQty = originalTickets[name]?.quantity;
                // Existing tiers on a published event: only the name is locked
                // and the quantity cannot be reduced. Price, description and
                // max-per-booking are editable.
                const isExistingPublished = isPublished && isExisting;
                return (
                  <div
                    key={key}
                    className="rounded-xl border p-5 bg-muted/20 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">
                        Ticket Tier {name + 1}
                        {isPublished && !isExisting && (
                          <span className="ml-2 text-xs font-normal text-green-600">New</span>
                        )}
                      </span>
                      {(!isPublished && fields.length > 1) || (isPublished && !isExisting) ? (
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<Trash2 className="h-4 w-4" />}
                          onClick={() => {
                            remove(name);
                            setTimeout(revalidate, 0);
                          }}
                        />
                      ) : null}
                    </div>

                    <Row gutter={[16, 0]}>
                      <Col span={24}>
                        <Form.Item
                          name={[name, "name"]}
                          label="Tier Name"
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message: "Tier name is required",
                            },
                            { validator: textInputValidator },
                          ]}
                        >
                          <Input
                            placeholder="General Admission, VIP, Early Bird…"
                            maxLength={50}
                            showCount
                            disabled={isExistingPublished}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          name={[name, "description"]}
                          label="Description"
                          rules={[{ validator: textInputValidator }]}
                        >
                          <Input.TextArea
                            placeholder="Brief description of this tier"
                            maxLength={225}
                            rows={2}
                            showCount
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                   

                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          name={[name, "price"]}
                          label="Price (₹)"
                          rules={[
                            {
                              required: true,
                              message: "Price is required",
                            },
                            {
                              validator(_, value) {
                                if (
                                  value === "" ||
                                  value === null ||
                                  value === undefined
                                )
                                  return Promise.resolve();
                                return Number(value) >= 0
                                  ? Promise.resolve()
                                  : Promise.reject(
                                      new Error("Price must be at least ₹0")
                                    );
                              },
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            min={0}
                            max={9999999}
                            step={1}
                            maxLength={7}
                            showCount
                            prefix="₹"
                            placeholder="0"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          name={[name, "quantity"]}
                          label={
                            isExistingPublished &&
                            origQty !== null &&
                            origQty !== undefined
                              ? `Quantity (current: ${origQty})`
                              : "Quantity"
                          }
                          rules={[
                            {
                              required: true,
                              message: "Quantity is required",
                            },
                            {
                              validator(_, value) {
                                if (
                                  value === "" ||
                                  value === null ||
                                  value === undefined
                                )
                                  return Promise.resolve();
                                const num = Number(value);
                                if (num < 1)
                                  return Promise.reject(
                                    new Error("Quantity must be at least 1")
                                  );
                                if (
                                  isExistingPublished &&
                                  origQty !== null &&
                                  num < origQty
                                )
                                  return Promise.reject(
                                    new Error(
                                      `Cannot decrease below ${origQty}. You may only increase.`
                                    )
                                  );
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            min={isExistingPublished ? (origQty ?? 1) : 1}
                            max={9999999}
                            step={1}
                            maxLength={7}
                            showCount
                            placeholder="100"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          name={[name, "max_concurrent_bookings"]}
                          label="Max tickets per booking"
                          initialValue={10}
                          rules={[
                            { required: true, message: "Required" },
                            {
                              type: "integer",
                              min: 1,
                              max: 10,
                              message: "Must be between 1 and 10",
                            },
                          ]}
                        >
                          <InputNumber
                            min={1}
                            max={10}
                            step={1}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                );
              })}

              {fields.length < 10 && (
                <Button
                  type="dashed"
                  onClick={() => {
                    add({
                      name: "",
                      description: "",
                      price: "",
                      quantity: "",
                      max_concurrent_bookings: 10,
                    });
                    setTimeout(revalidate, 0);
                  }}
                  block
                  icon={<Plus className="h-4 w-4" />}
                >
                  Add Ticket Tier
                </Button>
              )}
            </div>
          );
        }}
      </Form.List>
    </div>
  );
}

export default Step4Tickets;
